import { createSlice } from "@reduxjs/toolkit";
import secureLocalStorage from "react-secure-storage";

const getSavedPartnerId = () => {
  try {
    return secureLocalStorage.getItem("partner_id") || null;
  } catch {
    return null;
  }
};

const initialState = {
  // Registration and Authentication info
  partnerId: getSavedPartnerId(),
  mobileNumber: "",
  otpSent: false,
  isAuthenticated: false,

  // Profile data
  profile: {
    first_name: "",
    last_name: "",
    email_address: "",
    mobile_number: "",
    city: "",
    state: "",
    city_id: "",
    state_id: "",
    service_id: "",
    is_brand_ambassador: false,
    selected_subcategories: [],
  },

  // Service & Catalog Selection
  services: [],
  selectedService: null,
  subCategories: [],
  selectedSubCategories: [],
  loadingServices: false,
  loadingSubCategories: false,

  // Subscription state
  subscription: null,
  subscriptionStatus: "NONE", // 'NONE' | 'ACTIVE' | 'EXPIRED' | 'INACTIVE'
  subscriptionPlans: [],
  loadingSubscription: false,

  // Documents state
  documents: [],
  uploadedDocumentTypes: {},
  loadingDocuments: false,

  // Onboarding flow control
  currentStep: 1, // 1: Registration, 2: OTP, 3: Profile, 4: Service, 5: SubCategories, 6: Subscription, 7: Documents, 8: Verification
  verificationStatus: "NEW", // 'NEW' | 'DOCUMENTS_PENDING' | 'UNDER_REVIEW' | 'VERIFIED' | 'EXPIRED_SUBSCRIPTION'

  // General loading & error
  loading: false,
  error: null,
};

const partnerOnboardingSlice = createSlice({
  name: "partnerOnboarding",
  initialState,
  reducers: {
    setPartnerMobile: (state, action) => {
      state.mobileNumber = action.payload;
    },
    setOtpSent: (state, action) => {
      state.otpSent = action.payload;
    },
    setPartnerAuthenticated: (state, action) => {
      state.isAuthenticated = true;
      if (action.payload?.user?.id) {
        state.partnerId = action.payload.user.id;
        try {
          secureLocalStorage.setItem("partner_id", action.payload.user.id);
        } catch (e) {
          console.warn("Could not persist partner_id:", e);
        }
      }
      if (action.payload?.user) {
        const u = action.payload.user;
        state.profile = {
          ...state.profile,
          first_name: u.first_name || state.profile.first_name || "",
          last_name: u.last_name || state.profile.last_name || "",
          email_address: u.email_address || u.email || state.profile.email_address || "",
          mobile_number: u.mobile_number || state.profile.mobile_number || "",
          service_id: u.service_id ? String(u.service_id) : (state.profile.service_id || ""),
          city: u.cities?.[0]?.name || state.profile.city || "",
          state: u.cities?.[0]?.state?.name || state.profile.state || "",
        };
      }
    },
    updatePartnerProfileField: (state, action) => {
      state.profile = {
        ...state.profile,
        ...action.payload,
      };
    },
    setServicesList: (state, action) => {
      state.services = action.payload || [];
      state.loadingServices = false;
    },
    setLoadingServices: (state, action) => {
      state.loadingServices = action.payload;
    },
    setSelectedService: (state, action) => {
      state.selectedService = action.payload;
      state.profile.service_id = action.payload ? String(action.payload.id) : "";
    },
    setSubCategoriesList: (state, action) => {
      state.subCategories = action.payload || [];
      state.loadingSubCategories = false;
    },
    setLoadingSubCategories: (state, action) => {
      state.loadingSubCategories = action.payload;
    },
    toggleSubCategorySelection: (state, action) => {
      const catId = action.payload;
      const index = state.selectedSubCategories.indexOf(catId);
      if (index >= 0) {
        state.selectedSubCategories.splice(index, 1);
      } else {
        state.selectedSubCategories.push(catId);
      }
    },
    setSelectedSubCategories: (state, action) => {
      state.selectedSubCategories = action.payload || [];
    },
    setSubscriptionData: (state, action) => {
      state.subscription = action.payload;
      state.loadingSubscription = false;
      if (action.payload?.active || action.payload?.subscriptionPlanActive || action.payload?.status === "ACTIVE") {
        state.subscriptionStatus = "ACTIVE";
      } else if (action.payload?.status === "EXPIRED" || action.payload?.isExpired) {
        state.subscriptionStatus = "EXPIRED";
      } else {
        state.subscriptionStatus = "NONE";
      }
    },
    setSubscriptionPlansList: (state, action) => {
      state.subscriptionPlans = action.payload || [];
    },
    setLoadingSubscription: (state, action) => {
      state.loadingSubscription = action.payload;
    },
    setUploadedDocuments: (state, action) => {
      state.documents = action.payload || [];
      const typeMap = {};
      (action.payload || []).forEach((doc) => {
        const docName = String(doc.name || doc.document_type || "").toLowerCase().trim();
        if (docName) {
          typeMap[docName] = true;
        }
      });
      state.uploadedDocumentTypes = typeMap;
      state.loadingDocuments = false;
    },
    addUploadedDocument: (state, action) => {
      const doc = action.payload;
      state.documents.push(doc);
      const docName = String(doc.name || doc.document_type || "").toLowerCase().trim();
      if (docName) {
        state.uploadedDocumentTypes[docName] = true;
      }
    },
    setLoadingDocuments: (state, action) => {
      state.loadingDocuments = action.payload;
    },
    setOnboardingStep: (state, action) => {
      state.currentStep = action.payload;
    },
    nextOnboardingStep: (state) => {
      state.currentStep += 1;
    },
    prevOnboardingStep: (state) => {
      if (state.currentStep > 1) {
        state.currentStep -= 1;
      }
    },
    setVerificationStatus: (state, action) => {
      state.verificationStatus = action.payload;
    },
    setPartnerError: (state, action) => {
      state.error = action.payload;
      state.loading = false;
    },
    setPartnerLoading: (state, action) => {
      state.loading = action.payload;
    },
    resetPartnerOnboarding: () => initialState,
  },
});

export const {
  setPartnerMobile,
  setOtpSent,
  setPartnerAuthenticated,
  updatePartnerProfileField,
  setServicesList,
  setLoadingServices,
  setSelectedService,
  setSubCategoriesList,
  setLoadingSubCategories,
  toggleSubCategorySelection,
  setSelectedSubCategories,
  setSubscriptionData,
  setSubscriptionPlansList,
  setLoadingSubscription,
  setUploadedDocuments,
  addUploadedDocument,
  setLoadingDocuments,
  setOnboardingStep,
  nextOnboardingStep,
  prevOnboardingStep,
  setVerificationStatus,
  setPartnerError,
  setPartnerLoading,
  resetPartnerOnboarding,
} = partnerOnboardingSlice.actions;

export default partnerOnboardingSlice.reducer;
